import { type ChangeEvent, useEffect, useState } from "react";
import triggerLoading from "../../../lib/propagateSearchModalLoading";
import type { SelectedProvider } from "../../../types";

type Params = {
  open: boolean;
  currentSelectedProviders?: SelectedProvider[];
  onSearch?: (query: string) => void;
  onCategoryChange?: (category: string) => void;
  onSaveRecentSearch: (term: string) => void;
};

type Result = {
  search: string;
  category: string;
  activeProviderFilter: SelectedProvider[];
  scrollContainer: HTMLDivElement | null;
  setScrollContainer: (el: HTMLDivElement | null) => void;
  handleSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSearchBlur: () => void;
  handleClearSearch: () => void;
  selectRecentSearch: (term: string) => void;
  setCategory: (category: string) => void;
  toggleProviderTab: (provider: SelectedProvider) => void;
  clearProviderTab: () => void;
};

const useSearchModalView = ({
  open,
  currentSelectedProviders,
  onSearch,
  onCategoryChange,
  onSaveRecentSearch,
}: Params): Result => {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [activeProviderFilter, setActiveProviderFilter] = useState<
    SelectedProvider[]
  >([]);
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null,
  );

  useEffect(() => {
    if (!open) {
      setSearch("");
      setCategory("all");
      setActiveProviderFilter([]);
    }
  }, [open]);

  useEffect(() => {
    setActiveProviderFilter((prev) =>
      prev.filter((provider) =>
        currentSelectedProviders?.some((p) => p.name === provider.name),
      ),
    );
  }, [currentSelectedProviders]);

  useEffect(() => {
    const handler = setTimeout(() => {
      onCategoryChange?.(category);
      onSearch?.(search.trim());
    }, 300);
    let scrollTimer: ReturnType<typeof setTimeout> | undefined;
    if (scrollContainer) {
      scrollTimer = setTimeout(
        () => scrollContainer?.scrollTo?.({ top: 0, behavior: "smooth" }),
        100,
      );
    }
    return () => {
      clearTimeout(handler);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, [search, category]);

  function handleSearchChange(e: ChangeEvent<HTMLInputElement>) {
    triggerLoading(); // This is needed to give a fast feedback of the search...
    setSearch(e.target.value);
  }

  function handleSearchBlur() {
    if (typeof search === "string" && search.trim().length > 2) {
      onSaveRecentSearch(search);
    }
  }

  function handleClearSearch() {
    setSearch("");
    onSearch?.("");
  }

  function selectRecentSearch(term: string) {
    setSearch(term);
    onSearch?.(term);
  }

  function toggleProviderTab(provider: SelectedProvider) {
    setActiveProviderFilter((prev) =>
      prev.some((p) => p.name === provider.name)
        ? prev.filter((p) => p.name !== provider.name)
        : [...prev, provider],
    );
  }

  function clearProviderTab() {
    setActiveProviderFilter([]);
  }

  return {
    search,
    category,
    activeProviderFilter,
    scrollContainer,
    setScrollContainer,
    handleSearchChange,
    handleSearchBlur,
    handleClearSearch,
    selectRecentSearch,
    setCategory,
    toggleProviderTab,
    clearProviderTab,
  };
};

export default useSearchModalView;
